import React, { FC, useMemo, useState } from 'react'
import styled from 'styled-components'
import OverlayPage from 'components/dine-in/OverlayPage'
import SearchCategoriesAndProducts from 'components/SearchCategoriesAndProducts'
import { useTranslation } from 'react-i18next'
import {
  ProductListWrapper,
  ExcludedProductSection,
  ProductCard,
  SubTitle,
  ProductListSection,
  Divider,
} from './ProductListStyled'
import { MenuCategoryType } from 'types'
import ExcludedProduct from './ExcludedProduct'
import { BuffetPackageProducts } from 'hooks/buffet/useCategory'
import { ProductServiceEnum } from 'graphQL/dinein/generate/operations'
import { filterSpecialCharacters } from 'helpers/string'
import CartIcon from './CartIcon'

type Props = {
  isOpen: boolean
  onBack: () => void
  menuCategories: MenuCategoryType[]
  vendor: any
  onClickCategoryMenu: (category: MenuCategoryType) => void
  membershipProgram: any
  buffetProducts: BuffetPackageProducts
  onAddToCart: (product: any) => void
  onClickProduct: (product: any) => void
  buffetPackageID: number
}

const OverlaySearchProducts: FC<Props> = ({
  isOpen,
  onBack,
  menuCategories,
  vendor,
  onClickCategoryMenu,
  membershipProgram,
  buffetProducts,
  onAddToCart,
  onClickProduct,
  buffetPackageID,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const [keyword, setKeyword] = useState('')

  const { includedProducts, excludedProducts, addonProducts } = useMemo(() => {
    if (keyword) {
      const includedProducts: BuffetPackageProducts = []
      const excludedProducts: BuffetPackageProducts = []
      const addonProducts: BuffetPackageProducts = []
      const _keyword = filterSpecialCharacters(keyword).toLowerCase()
      buffetProducts.forEach((product) => {
        const inPackage = product.buffetPackageProducts.find((p) => p.buffetPackageID === buffetPackageID)
        const isMatching =
          product.nameEn.toLowerCase().includes(_keyword) || product.nameTh.toLowerCase().includes(_keyword)
        if (isMatching && product.active) {
          if (inPackage) {
            includedProducts.push(product)
          } else if (product.availableServices.includes(ProductServiceEnum.BuffetALaCarte)) {
            addonProducts.push(product)
          } else {
            excludedProducts.push(product)
          }
        }
      })
      return { includedProducts, addonProducts, excludedProducts }
    } else {
      return {
        includedProducts: [],
        addonProducts: [],
        excludedProducts: [],
      }
    }
  }, [keyword, buffetPackageID, buffetProducts])

  return (
    <OverlayPage isShow={isOpen} animation>
      <SearchCategoriesAndProducts
        show={isOpen}
        menuCategories={menuCategories}
        listProducts={[]}
        onClickCategoryMenu={(data) => {
          setKeyword('')
          onClickCategoryMenu(data)
        }}
        onClickProduct={() => {}}
        onBack={() => {
          setKeyword('')
          onBack()
        }}
        vendor={vendor}
        membershipProgram={membershipProgram}
        handleAddToCart={() => {}}
        onKeywordChange={(keyword) => setKeyword(keyword)}
      >
        <ProductListSection id="product-search-result" style={{ paddingBottom: 40 }}>
          {includedProducts.length > 0 && (
            <div>
              <SubTitle>{t('buffet.productListPage.includedProducts')}</SubTitle>
              <ProductListWrapper>
                {includedProducts.map((product) => (
                  <ProductCard
                    key={product.nameEn}
                    overflow
                    addToCart
                    vendor={vendor}
                    lang={language}
                    data={product}
                    onAddToCart={onAddToCart}
                    onClickProduct={onClickProduct}
                    addToCartIconName="plus-2"
                    membershipProgram={membershipProgram}
                    nameCustomClass="product-name"
                    addToCartIconClass="add-to-cart-icon"
                    isOutline
                    hideZeroPrice
                  />
                ))}
              </ProductListWrapper>
            </div>
          )}

          {includedProducts.length > 0 && addonProducts.length > 0 && <Divider />}

          {addonProducts.length > 0 && (
            <div>
              <SubTitle>{t('buffet.productListPage.addonProducts')}</SubTitle>
              <ProductListWrapper>
                {addonProducts.map((product) => (
                  <ProductCard
                    key={product.nameEn}
                    overflow
                    addToCart
                    vendor={vendor}
                    lang={language}
                    data={product}
                    onAddToCart={onAddToCart}
                    onClickProduct={onClickProduct}
                    addToCartIconName="plus-2"
                    membershipProgram={membershipProgram}
                    nameCustomClass="product-name"
                    addToCartIconClass="add-to-cart-icon"
                    isOutline
                  />
                ))}
              </ProductListWrapper>
            </div>
          )}
          {addonProducts.length > 0 && excludedProducts.length > 0 && <Divider />}

          {excludedProducts.length > 0 && (
            <ExcludedProductSection isShowOnlyExcludedProducts={true} style={{ padding: 0 }}>
              <SubTitle>{t('buffet.productListPage.excludedProducts')}</SubTitle>
              <ProductListWrapper>
                {excludedProducts.map((product) => (
                  <ExcludedProduct key={product.id} product={product} onClick={onClickProduct} />
                ))}
              </ProductListWrapper>
            </ExcludedProductSection>
          )}
        </ProductListSection>
      </SearchCategoriesAndProducts>
      <CartIconWrapper id="search-product-cart-quantity">
        <CartIcon />
      </CartIconWrapper>
    </OverlayPage>
  )
}

export default OverlaySearchProducts

const CartIconWrapper = styled.div`
  z-index: 99;
  position: fixed;
  bottom: 15px;
  right: 15px;
`
